import {KTCard, toAbsoluteUrl} from '_metronic/helpers'
import StatusBadge from 'app/components/StatusBadge'
import {useAuth} from 'app/modules/auth'
import React, {useEffect, useState} from 'react'
import db from 'services/firebase'
import {
  collection,
  doc,
  onSnapshot,
  deleteField,
  deleteDoc,
  updateDoc,
  serverTimestamp,
} from 'firebase/firestore'
import {Link, useLocation, useNavigate, useParams} from 'react-router-dom'
import ReviewFilterBar from '../components/Filters/ReviewFilterBar'
import EmptyState from 'app/components/EmptyState'
import StartReviewModal from '../components/Modals/StartReviewModal'
import {ToastContainer} from 'react-toastify'
import ActionDropdown from '../components/ActionDropdown'
import {showErrorToast, showSuccessToast} from 'app/components/ShowToast'
import OwnerDisplay from 'app/components/OwnerDisplay'
import {LocationState} from '../utils/PagesUtils'
import ReviewHeader from '../components/ReviewHeader'

const VendorReviewDetails = () => {
  const [sortBy, setSortBy] = useState('system')
  const [sortDirection, setSortDirection] = useState('asc')
  const [filteredItems, setFilteredItems] = useState<any[]>([])
  const {currentUser} = useAuth()
  const [reviews, setReviews] = useState<any[]>([])
  const {id} = useParams()
  const [owners, setOwners] = useState<any[]>([])
  const [searchTerm, setSearchTerm] = useState('')
  const [selectReviewer, setSelectReviewer] = useState('')
  const [selectStatus, setSelectStatus] = useState('')
  const [isLoading, setIsLoading] = useState(true)
  const [modalOpen, setModalOpen] = useState(false)
  const [systemId, setSystemId] = useState('')
  const [dueDate, setDueDate] = useState('')
  const [reviewer, setReviewer] = useState<{
    id: string
    name: string
    email: string
  } | null>(null)
  const navigate = useNavigate()
  const location = useLocation()
  const {toast} = (location.state as LocationState) || {}
  const [activeTab, setActiveTab] = useState('systems')

  useEffect(() => {
    if (toast) {
      const {message, type} = toast
      if (type === 'success') {
        showSuccessToast({message})
      } else if (type === 'error') {
        showErrorToast({message})
      }
      window.history.replaceState({}, document.title, location.pathname)
    }
  }, [toast])

  useEffect(() => {
    if (location.pathname.includes('access-changes')) {
      setActiveTab('access-changes')
    } else {
      setActiveTab('systems')
    }
  }, [location.pathname, id])

  useEffect(() => {
    getReviews()
  }, [id])

  const getReviews = () => {
    try {
      const tenantId = currentUser?.tenantId || ''
      if (!tenantId || !id) {
        setIsLoading(false)
        return
      }
      const tenantRef = doc(db, 'tenants', tenantId)
      const PolicyRef = collection(tenantRef, 'reviews')

      const unsubscribe = onSnapshot(doc(PolicyRef, id), (snapshot) => {
        const policyData = snapshot.data()
        setReviews(policyData ? [{id: snapshot.id, ...policyData}] : [])
        setIsLoading(false)
      })

      return unsubscribe
    } catch (error) {
      console.error('Error getting policy:', error)
      setIsLoading(false)
    }
  }

  useEffect(() => {
    let filtered = reviews
      .map((review) => ({
        ...review,
        systems: Object.entries(review.systems || {}).map(([systemId, system]: [string, any]) => ({
          systemId,
          ...system,
        })),
      }))
      .flatMap((review) =>
        review.systems.map((system) => ({
          ...review,
          system,
        }))
      )

    if (searchTerm) {
      filtered = filtered.filter((item) => {
        const system = item.system
        return (
          system?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          system?.domain?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          system?.reviewer?.name?.toLowerCase().includes(searchTerm.toLowerCase())
        )
      })
    }

    if (selectReviewer) {
      filtered = filtered.filter((item) => item.system?.reviewer?.name === selectReviewer)
    }

    if (selectStatus) {
      filtered = filtered.filter((item) => item.status === selectStatus)
    }

    const sortedItems = filtered.sort((a, b) => {
      if (sortBy === 'system') {
        const valueA = a.system?.name || ''
        const valueB = b.system?.name || ''
        return sortDirection === 'asc' ? valueA.localeCompare(valueB) : valueB.localeCompare(valueA)
      } else if (sortBy === 'reviewer' || sortBy === 'accessData' || sortBy === 'status') {
        const valueA = sortBy === 'reviewer' ? a.system?.reviewer?.name || '' : a[sortBy] || ''
        const valueB = sortBy === 'reviewer' ? b.system?.reviewer?.name || '' : b[sortBy] || ''
        return sortDirection === 'asc' ? valueA.localeCompare(valueB) : valueB.localeCompare(valueA)
      } else if (sortBy === 'createdAt' || sortBy === 'updatedAt') {
        const dateA = a[sortBy] ? new Date(a[sortBy].seconds * 1000).getTime() : 0
        const dateB = b[sortBy] ? new Date(b[sortBy].seconds * 1000).getTime() : 0
        return sortDirection === 'asc' ? dateA - dateB : dateB - dateA
      }
      return 0
    })

    setFilteredItems(sortedItems)
  }, [reviews, searchTerm, selectReviewer, selectStatus, sortBy, sortDirection])

  const handleSort = (column: string) => {
    const newSortDirection = column === sortBy && sortDirection === 'asc' ? 'desc' : 'asc'
    setSortBy(column)
    setSortDirection(newSortDirection)
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const searchTerm = e.target.value
    setSearchTerm(searchTerm)
  }

  const getStatusBadgeStyles = (status: 'connected' | 'disconnected') => {
    switch (status) {
      case 'connected':
        return {
          badgeClass: 'badge-primary',
          color: '#0E9F6E',
          background: '#E8F8F4',
        }
      case 'disconnected':
        return {
          badgeClass: 'badge-danger',
          color: '#EE3043',
          background: '#FFF5F8',
        }
      default:
        return {
          badgeClass: 'badge-primary',
          color: '#0E9F6E',
          background: '#E8F8F4',
        }
    }
  }

  const handleOpenModal = (
    dueDate: string,
    id: string,
    reviewer: {id: string; name: string; email: string}
  ) => {
    setModalOpen(true)
    setSystemId(id)
    setDueDate(dueDate)
    setReviewer(reviewer)
  }

  const handleCloseModal = () => {
    setModalOpen(false)
    setSystemId('')
  }

  const handleDelete = async (reviewId: string, systemId?: string, isReview?: boolean) => {
    try {
      const tenantId = currentUser?.tenantId || ''
      if (!tenantId) {
        return
      }

      if (isReview) {
        const reviewRef = doc(db, 'tenants', tenantId, 'reviews', reviewId)
        await deleteDoc(reviewRef)
        navigate('/access/review', {
          state: {toast: {message: 'Review deleted successfully', type: 'success'}},
        })
        showSuccessToast({message: 'Review deleted successfully'})
      } else if (systemId) {
        const reviewRef = doc(db, 'tenants', tenantId, 'reviews', reviewId)
        await updateDoc(reviewRef, {
          [`systems.${systemId}`]: deleteField(),
        })
        showSuccessToast({message: 'System removed successfully'})
      }
    } catch (error) {
      showErrorToast({message: 'Failed to delete. Please try again.'})
    }
  }

  const isCompleteButtonDisabled =
    reviews.length > 0 &&
    reviews[0].systems &&
    Object.values(reviews[0].systems).some((system: any) => system.status !== 'Completed')

  const handleComplete = async () => {
    const tenantId = currentUser?.tenantId || ''
    const testDocRef = doc(db, `tenants/${tenantId}/reviews/${id}`)

    await updateDoc(testDocRef, {
      status: 'Completed',
      updatedAt: serverTimestamp(),
      dueDate: serverTimestamp(),
      completedAt: serverTimestamp(),
    })
    navigate('/access/review', {
      state: {toast: {message: 'Review completed successfully', type: 'success'}},
    })
  }

  return (
    <>
      <ReviewHeader
        reviews={reviews}
        activeTab={activeTab}
        filteredItems={filteredItems}
        isCompleteButtonDisabled={isCompleteButtonDisabled}
        handleOpenModal={handleOpenModal}
        handleComplete={handleComplete}
        handleDelete={handleDelete}
      />

      <KTCard>
        <ReviewFilterBar
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          owners={owners}
          selectedPlatform={selectReviewer}
          setSelectedPlatform={setSelectReviewer}
          selectedStatus={selectStatus}
          setSelectedStatus={setSelectStatus}
          handleInputChange={handleInputChange}
          type='reviewDetail'
        />
        <div className='card-body py-3'>
          <div className='table-responsive' style={{minHeight: '50vh', overflowY: 'auto'}}>
            <table className='table table-row-dashed table-row-gray-300 align-middle gs-0 gy-4'>
              <thead>
                <tr className='fw-bold' style={{color: '#5A5A5A'}}>
                  <th className='min-w-300px' onClick={() => handleSort('system')}>
                    SYSTEM{' '}
                    {sortBy === 'system' && (
                      <span
                        className={`ms-1 icon-xxs text-muted ${
                          sortDirection === 'asc' ? 'bi bi-arrow-up' : 'bi bi-arrow-down'
                        }`}
                      />
                    )}
                  </th>
                  <th className='min-w-200px' onClick={() => handleSort('reviewer')}>
                    REVIEWER{' '}
                    {sortBy === 'reviewer' && (
                      <span
                        className={`ms-1 icon-xxs text-muted ${
                          sortDirection === 'asc' ? 'bi bi-arrow-up' : 'bi bi-arrow-down'
                        }`}
                      />
                    )}
                  </th>
                  <th className='min-w-200px' onClick={() => handleSort('accessData')}>
                    ACCESS DATA{' '}
                    {sortBy === 'accessData' && (
                      <span
                        className={`ms-1 icon-xxs text-muted ${
                          sortDirection === 'asc' ? 'bi bi-arrow-up' : 'bi bi-arrow-down'
                        }`}
                      />
                    )}
                  </th>
                  <th className='min-w-200px' onClick={() => handleSort('status')}>
                    STATUS{' '}
                    {sortBy === 'status' && (
                      <span
                        className={`ms-1 icon-xxs text-muted ${
                          sortDirection === 'asc' ? 'bi bi-arrow-up' : 'bi bi-arrow-down'
                        }`}
                      />
                    )}
                  </th>
                  <th className='min-w-0px'></th>
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  <tr style={{height: '100%'}}>
                    <td colSpan={5}>
                      <EmptyState
                        title='Loading Reviews'
                        description='Please wait while we fetch the review details'
                        isLoading={true}
                      />
                    </td>
                  </tr>
                ) : filteredItems.length > 0 ? (
                  filteredItems.map((item) => {
                    const system = item.system
                    return (
                      <tr key={`${item.id}-${system.systemId}`}>
                        <td>
                          <div className='d-flex align-items-center gap-2'>
                            {system?.icon && (
                              <img
                                src={toAbsoluteUrl(system.icon)}
                                alt={system.name}
                                className='me-2'
                                style={{width: '24px', height: '24px'}}
                              />
                            )}
                            <div>
                              {item.status === 'Pending' ? (
                                <span
                                  className='fw-semibold d-block fs-6 text-dark'
                                  style={{cursor: 'not-allowed', opacity: 0.8}}
                                >
                                  {system?.name || '--'}
                                </span>
                              ) : (
                                <Link
                                  to={`/access/review/${id}/system/${system.systemId}`}
                                  className='fw-semibold d-block fs-6 text-dark text-hover-primary'
                                  style={{cursor: 'pointer'}}
                                >
                                  {system?.name || '--'}
                                </Link>
                              )}
                              <span className='text-gray-800 fs-7'>{system?.domain || '--'}</span>
                            </div>
                          </div>
                        </td>
                        <td>
                          <span className='text-dark fw-semibold d-block fs-6'>
                            {system?.reviewer ? (
                              <OwnerDisplay owner={system.reviewer.name} />
                            ) : (
                              '--'
                            )}
                          </span>
                        </td>
                        <td>
                          <div className='d-flex align-items-start'>
                            {system?.integrationStatus ? (
                              <span
                                className={`badge fs-7 ${
                                  getStatusBadgeStyles(system.integrationStatus).badgeClass
                                }`}
                                style={{
                                  color: getStatusBadgeStyles(system.integrationStatus).color,
                                  background: getStatusBadgeStyles(system.integrationStatus)
                                    .background,
                                }}
                              >
                                {system.integrationStatus === 'connected' ? 'Synced' : 'Inactive'}
                              </span>
                            ) : (
                              '--'
                            )}
                          </div>
                        </td>
                        <td>
                          <span className='fw-semibold fs-6'>
                            <StatusBadge
                              status={
                                system.status as
                                  | 'Active'
                                  | 'Inactive'
                                  | 'Deactivated'
                                  | 'Pending'
                                  | 'Due'
                                  | 'Completed'
                                  | 'Not Started'
                                  | 'In review'
                              }
                            />
                          </span>
                        </td>
                        <td>
                          <ActionDropdown
                            reviewId={item.id}
                            systemId={system.systemId}
                            onDelete={() => handleDelete(item.id, system.systemId, false)}
                            status={system.status}
                          />
                        </td>
                      </tr>
                    )
                  })
                ) : (
                  <tr style={{height: '100%'}}>
                    <td colSpan={5}>
                      <EmptyState
                        title='No Reviews Found'
                        description='No reviews match your current filters or search criteria'
                      />
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </KTCard>
      <StartReviewModal
        isOpen={modalOpen}
        onClose={handleCloseModal}
        id={systemId}
        dueDate={dueDate}
        reviewer={reviewer || {id: '', name: '', email: ''}}
      />
      <ToastContainer />
    </>
  )
}

export default VendorReviewDetails
